import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { api, errMsg } from '@/lib/api';

export const MODS = {
  summary: { key: 'summary', label: { id: 'Ringkasan Eksekutif', en: 'Executive Summary' }, color: '#1B2A6B', soft: 'rgba(27,42,107,0.08)' },
  sales: { key: 'sales', label: { id: 'Sales', en: 'Sales' }, color: '#2563EB', soft: 'rgba(37,99,235,0.10)' },
  ecommerce: { key: 'ecommerce', label: { id: 'E-commerce', en: 'E-commerce' }, color: '#EA580C', soft: 'rgba(234,88,12,0.10)' },
  pos: { key: 'pos', label: { id: 'POS', en: 'POS' }, color: '#DC2626', soft: 'rgba(220,38,38,0.10)' },
  inventory: { key: 'inventory', label: { id: 'Inventory', en: 'Inventory' }, color: '#16A34A', soft: 'rgba(22,163,74,0.10)' },
  pillot_pay: { key: 'pillot_pay', label: { id: 'Pillot Pay', en: 'Pillot Pay' }, color: '#CA8A04', soft: 'rgba(202,138,4,0.12)' },
  pesan: { key: 'pesan', label: { id: 'Pesan', en: 'Messages' }, color: '#7C3AED', soft: 'rgba(124,58,237,0.10)' },
  hr: { key: 'hr', label: { id: 'HR', en: 'HR' }, color: '#DB2777', soft: 'rgba(219,39,119,0.10)' },
  finance: { key: 'finance', label: { id: 'Keuangan', en: 'Finance' }, color: '#0F766E', soft: 'rgba(15,118,110,0.10)' },
  purchasing: { key: 'purchasing', label: { id: 'Pembelian', en: 'Purchasing' }, color: '#B45309', soft: 'rgba(180,83,9,0.10)' },
  crm: { key: 'crm', label: { id: 'CRM', en: 'CRM' }, color: '#4F46E5', soft: 'rgba(79,70,229,0.10)' },
  email_marketing: { key: 'email_marketing', label: { id: 'Pemasaran Email', en: 'Email Marketing' }, color: '#E11D48', soft: 'rgba(225,29,72,0.10)' },
};
export const QUICK_QUESTIONS = {
  summary: ['Kenapa pendapatan berubah dibanding periode sebelumnya?', 'Apakah target bulan ini akan tercapai?'],
  sales: ['Kenapa penjualan turun/naik periode ini?', 'Produk apa yang paling menguntungkan?'],
  ecommerce: ['Kapan waktu ramai order toko online?', 'Order mana yang berisiko dibatalkan?'],
  pos: ['Metode pembayaran apa yang paling sering dipakai?', 'Jam atau hari apa POS paling ramai?'],
  inventory: ['Produk apa yang harus segera dipesan ulang?', 'Berapa lama stok produk terlaris akan habis?'],
  pillot_pay: ['Apakah saldo sudah layak dicairkan?'],
  pesan: ['Channel mana yang paling aktif dan bagaimana respons tim?'],
  hr: ['Bagaimana komposisi karyawan saat ini?'],
  finance: ['Kenapa laba bersih berubah?', 'Siapa debitur yang harus ditagih dulu?'],
  purchasing: ['Tagihan vendor mana yang harus dibayar dulu?'],
  crm: ['Bagaimana kondisi pipeline dan peluang terbesar?'],
  email_marketing: ['Bagaimana performa email marketing dan apa yang bisa ditingkatkan?'],
};
export const DashCtx = createContext({ drill: () => {}, ask: () => {}, lang: 'id' });
export const useDash = () => useContext(DashCtx);
/** Navy reads as black on dark backgrounds; swap to a light accent. */
export const tone = (color, dark) => (dark && color === '#1B2A6B' ? '#8FA8FF' : color);
export const PALETTE = ['#2563EB', '#16A34A', '#F59E0B', '#DC2626', '#7C3AED', '#0891B2', '#DB2777', '#64748B'];
export const PERIODS = [
  { key: 'today', id: 'Hari ini', en: 'Today' }, { key: 'week', id: 'Minggu ini', en: 'This week' }, { key: 'month', id: 'Bulan ini', en: 'This month' },
  { key: 'quarter', id: '3 bulan', en: '3 months' }, { key: 'year', id: 'Tahun ini', en: 'This year' }, { key: 'custom', id: 'Custom', en: 'Custom' },
];

export const idrShort = (n) => {
  const v = Number(n || 0);
  const a = Math.abs(v);
  if (a >= 1e9) return `Rp ${(v / 1e9).toLocaleString('id-ID', { maximumFractionDigits: 1 })} M`;
  if (a >= 1e6) return `Rp ${(v / 1e6).toLocaleString('id-ID', { maximumFractionDigits: 1 })} jt`;
  if (a >= 1e3) return `Rp ${(v / 1e3).toLocaleString('id-ID', { maximumFractionDigits: 0 })} rb`;
  return `Rp ${v.toLocaleString('id-ID')}`;
};
export const idrFull = (n) => 'Rp ' + Number(n || 0).toLocaleString('id-ID');
export const num = (n) => Number(n || 0).toLocaleString('id-ID');
export const dayLabel = (iso, lang = 'id') => new Date(iso + 'T00:00:00').toLocaleDateString(lang === 'id' ? 'id-ID' : 'en-US', { day: 'numeric', month: 'short' });
export const secLabel = (s, lang = 'id') => {
  if (s == null) return '—';
  if (s < 60) return `${s} ${lang === 'id' ? 'dtk' : 's'}`;
  if (s < 3600) return `${Math.round(s / 60)} ${lang === 'id' ? 'mnt' : 'min'}`;
  return `${(s / 3600).toLocaleString('id-ID', { maximumFractionDigits: 1 })} ${lang === 'id' ? 'jam' : 'h'}`;
};

/** Dense daily series → weekly / monthly buckets when the period is long, so charts stay readable. */
export const bucket = (series, keys, lang = 'id') => {
  if (!series?.length) return [];
  const n = series.length;
  if (n <= 45) return series.map((p) => ({ ...p, label: dayLabel(p.date, lang) }));
  const monthly = n > 190;
  const out = new Map();
  series.forEach((p) => {
    const d = new Date(p.date + 'T00:00:00');
    const k = monthly ? p.date.slice(0, 7) : (() => { const m = new Date(d); m.setDate(d.getDate() - ((d.getDay() + 6) % 7)); return m.toISOString().slice(0, 10); })();
    const row = out.get(k) || { date: k, label: monthly ? d.toLocaleDateString(lang === 'id' ? 'id-ID' : 'en-US', { month: 'short', year: '2-digit' }) : dayLabel(k, lang), ...Object.fromEntries(keys.map((x) => [x, 0])) };
    keys.forEach((x) => { row[x] += Number(p[x] || 0); });
    if (p.balance != null) row.balance = p.balance;
    out.set(k, row);
  });
  return [...out.values()];
};

export const useExport = (companyId, periodQuery) => {
  const [busy, setBusy] = useState('');
  const run = async (fmt, section = 'all') => {
    setBusy(`${section}-${fmt}`);
    try {
      const res = await api.get(`/companies/${companyId}/dashboard/export`, { params: { fmt, section, ...periodQuery }, responseType: 'blob' });
      const cd = res.headers['content-disposition'] || '';
      const name = (cd.match(/filename="?([^";]+)"?/) || [])[1] || `dashboard.${fmt}`;
      const url = URL.createObjectURL(res.data);
      const a = Object.assign(document.createElement('a'), { href: url, download: name });
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) {
      const d = e?.response?.data;
      toast.error(d instanceof Blob ? await d.text().then((t) => { try { return JSON.parse(t).detail; } catch { return t; } }) : errMsg(e));
    } finally { setBusy(''); }
  };
  return { run, busy };
};

/** requestAnimationFrame counter (no library). */
export const useCounter = (target, duration = 1100) => {
  const [val, setVal] = useState(0);
  const from = useRef(0);
  useEffect(() => {
    const start = performance.now();
    const a = from.current;
    const b = Number.isFinite(Number(target)) ? Number(target) : 0;
    let raf;
    const tick = (t) => {
      const k = Math.min(1, (t - start) / duration);
      const ease = 1 - Math.pow(1 - k, 3);
      setVal(a + (b - a) * ease);
      if (k < 1) raf = requestAnimationFrame(tick); else from.current = b;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return val;
};
